import { useMemo } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bot, Building2, ExternalLink, LayoutGrid, Link2, Megaphone, ToggleRight, Users } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { isMasterEmail } from '@/shared/lib/navigation';
import { usePassbookQuery } from '@/shared/lib/passbook';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Skeleton } from '@/shared/ui/skeleton';
import { PassbookLogoThumb } from '@/shared/components/passbook-logo-thumb';
import { usePermissionPassbook } from '@/features/permissions/api/use-permissions';
import { ModulesTable } from '@/features/permissions/ui/modules-table';
import { PageFlagsTable } from '@/features/permissions/ui/page-flags-table';
import { PassbookAiPanel } from '@/features/permissions/ui/passbook-ai-panel';
import { PassbookUsersTab } from '@/features/panel-users/ui/passbook-users-tab';
import { CLIENT_KIND_LABELS, CLIENT_LIFECYCLE_LABELS, fetchClientDetail } from '../api/use-clients';
import { ClientFormPage } from './client-form-page';
import { ClientMarketingTab } from './client-marketing-tab';

export type ClientHubTab = 'dados' | 'recursos' | 'paginas' | 'usuarios' | 'ia' | 'marketing';

const TABS: Array<{ value: ClientHubTab; label: string; icon: typeof Building2 }> = [
  { value: 'dados', label: 'Dados', icon: Building2 },
  { value: 'recursos', label: 'Recursos', icon: ToggleRight },
  { value: 'paginas', label: 'Páginas', icon: LayoutGrid },
  { value: 'usuarios', label: 'Usuários', icon: Users },
  { value: 'ia', label: 'IA', icon: Bot },
  { value: 'marketing', label: 'Marketing', icon: Megaphone },
];

function isClientHubTab(value: string | null): value is ClientHubTab {
  return TABS.some((tab) => tab.value === value);
}

/**
 * Tudo de um cliente num lugar só: empresa, recursos do programa, páginas do
 * menu, usuários do painel, IA e e-mail/SMS marketing. O passbook em foco é o
 * primeiro do cliente (a aba Marketing deixa escolher outro).
 */
export function ClientHubPage() {
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', id],
    queryFn: () => fetchClientDetail(id),
    enabled: Boolean(id),
  });

  const client = data?.client;
  const passbookRef = useMemo(
    () => client?.passbooks?.find((p) => !p.onlyShortUrl) ?? client?.passbooks?.[0],
    [client?.passbooks],
  );
  const passbookId = passbookRef?._id ?? '';
  const { data: row } = usePermissionPassbook(passbookId || null);
  const { data: passbook } = usePassbookQuery(passbookId, { enabled: Boolean(passbookId) });

  const isDraft = client?.registrationStatus === 'draft';
  // Recursos, Páginas, Usuários, IA e Marketing: só o master. Admin comum fica em Dados.
  const configurable = Boolean(passbookId) && !isDraft && isMasterEmail(user?.email);
  const requested = params.get('tab');
  const tab: ClientHubTab = configurable && isClientHubTab(requested) ? requested : 'dados';
  const setTab = (next: ClientHubTab) => {
    const copy = new URLSearchParams(params);
    if (next === 'dados') copy.delete('tab');
    else copy.set('tab', next);
    setParams(copy, { replace: true });
  };

  if (!user || user.role !== 'Admin') return <Navigate to="/login" replace />;
  if (isLoading) return <Skeleton className="h-96 rounded-2xl" />;
  if (isError || !client) return <Navigate to="/clientes" replace />;

  const shortUrl = passbookRef?.shortUrl;

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <PassbookLogoThumb
            passbookId={passbookId || undefined}
            name={client.name}
            backgroundColor={passbookRef?.backgroundColor}
            className="size-12"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight">{client.name || 'Sem nome'}</h1>
              {isDraft ? <Badge variant="warning">Rascunho</Badge> : null}
              <Badge variant={client.inactive ? 'destructive' : 'success'}>{client.inactive ? 'Inativo' : 'Ativo'}</Badge>
              {client.lifecycleStatus ? (
                <Badge variant="secondary" className="font-normal">
                  {CLIENT_LIFECYCLE_LABELS[client.lifecycleStatus]}
                </Badge>
              ) : null}
              {client.clientKind ? (
                <Badge variant="outline" className="font-normal">
                  {CLIENT_KIND_LABELS[client.clientKind]}
                </Badge>
              ) : null}
            </div>
            <p className="truncate text-sm text-muted-foreground">
              {[passbookRef?.organizationName, row ? `${row.storeCount} ${row.storeCount === 1 ? 'loja' : 'lojas'}` : null]
                .filter(Boolean)
                .join(' · ') || client.email}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {shortUrl ? (
            <Button variant="secondary" asChild>
              <a href={shortUrl} target="_blank" rel="noreferrer">
                <Link2 className="size-4" /> Fixe.Club
              </a>
            </Button>
          ) : null}
          {passbookId ? (
            <Button asChild>
              <Link to={`/loja/${passbookId}`}>
                <ExternalLink className="size-4" /> Inspecionar
              </Link>
            </Button>
          ) : null}
        </div>
      </header>

      {configurable ? (
        <SegmentedControl
          value={tab}
          onChange={setTab}
          options={TABS.map((item) => ({ value: item.value, label: item.label, icon: item.icon }))}
          size="sm"
        />
      ) : null}

      {tab === 'dados' ? <ClientFormPage embedded /> : null}
      {tab === 'recursos' ? (
        row && passbook ? (
          <ModulesTable idPassbook={passbookId} passbook={passbook} row={row} />
        ) : (
          <Skeleton className="h-96 rounded-2xl" />
        )
      ) : null}
      {tab === 'paginas' ? (row ? <PageFlagsTable passbook={row} /> : <Skeleton className="h-96 rounded-2xl" />) : null}
      {tab === 'usuarios' ? (row ? <PassbookUsersTab passbook={row} /> : <Skeleton className="h-96 rounded-2xl" />) : null}
      {tab === 'ia' ? (row ? <PassbookAiPanel passbook={row} /> : <Skeleton className="h-96 rounded-2xl" />) : null}
      {tab === 'marketing' ? (
        <ClientMarketingTab passbooks={(client.passbooks ?? []).filter((p) => !p.onlyShortUrl)} defaultId={passbookId} />
      ) : null}
    </div>
  );
}
